import React, { useState } from "react"
import { Form, Dropdown } from "react-bootstrap";
import caretdown from "../../../assets/images/caretdown.svg";
import crossIcon from "../../../assets/images/cross-icon.svg";

export type OptionType = {
    id: string | number;
    label: string;
    value: string;
}

type SelectFieldType = {
    data: Array<OptionType>;
    selectedValue?: string[] | number[] | string | number;
    onChange: (value: string | number, option: OptionType) => void;
    withSearch?: boolean;
    multiselect?: boolean;
    label: string;
    disabled?: boolean;
    onSearch?: (value: string) => Promise<void>;
    as?: "typehead" | "select";
    searchStatus?: "loading" | "error" | "success";
};

type DropDownItemType = {
    option: OptionType;
    isSearch?: boolean;
    handleSearch?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onChange?: (value: string | number, option: OptionType) => void;
    searchValue?: string;
} & Pick<SelectFieldType, "as" | "multiselect" | "selectedValue">

type DropDownToggleType = {
    handleSearch?: (e: React.ChangeEvent<HTMLInputElement>) => void;
} & Pick<SelectFieldType, "as" | "disabled" | "label">

const DropDownToggle = ({ label, disabled, as, handleSearch }: DropDownToggleType) => {
    if(as === "typehead") {
        return (
            <Dropdown.Toggle 
                className="form-control"
                as="input" 
                disabled={disabled}
                placeholder={label}
                // @ts-ignore
                type="search" 
                // @ts-ignore
                onChange={handleSearch}
            />
        )
    }

    return (
        <Dropdown.Toggle className="custom-dropdown-toggle" disabled={disabled}>
            {label}
            <img src={caretdown} alt="Dropdown icon" />
        </Dropdown.Toggle>
    )
}

const DropDownItem = ({ option, isSearch, handleSearch, onChange, multiselect, selectedValue, as, searchValue = "" }: DropDownItemType) => {
    const { id, label } = option;

    if(isSearch) {
        return (
            <Dropdown.Item 
                as="input" 
                type="search" 
                placeholder={option.label} 
                onClick={(e) => e.stopPropagation()} 
                onChange={handleSearch}
            />
        )
    }

    if(multiselect && as !== "typehead") {
        const Label = () => <span style={{ cursor: "pointer", marginLeft: "5px" }}>{label}</span>
        const isChecked = Array.isArray(selectedValue) ? selectedValue.includes(id as never) : selectedValue === id;

        return (
            <Dropdown.Item as="button" onClick={(e) => e.stopPropagation()}>
                <Form.Check
                    type="checkbox"
                    id={`dropdown-${id}`}
                    onChange={(e) => onChange?.(id, option)}
                    checked={isChecked}
                    className="fscclaim-dropdown"
                    label={<Label />}
                />
            </Dropdown.Item>
        )
    }

    if(as === "typehead") {
        const matchIndex = label.toLowerCase().indexOf(searchValue.toLowerCase());

        return (
            <Dropdown.Item as="button" onClick={() => onChange?.(id, option)}>
                {label.slice(0, matchIndex)}
                <span style={{ fontWeight: "bold" }}>{label.slice(matchIndex, matchIndex + searchValue.length)}</span>
                {label.slice(matchIndex + searchValue.length)}
            </Dropdown.Item>
        )
    }

    return (
        <Dropdown.Item as="button" onClick={() => onChange?.(id, option)}>
            {label}
        </Dropdown.Item>
    )
}

const SelectedPill = ({ selected, data, onChange }: { selected: string | number, data: Array<OptionType>, onChange: (value: string | number, option: OptionType) => void }) => {
    const selectedOption = data.find(option => option.id === selected);
    if(!selectedOption) return null;

    return (
        <div key={selected} className="custom-dropdown-selected-pill">
            <span className="me-2">{selectedOption.label}</span>
            <button className="custom-dropdown-selected-pill-close-btn" onClick={() => onChange?.(selected, selectedOption)}>
                <img src={crossIcon} alt="Close" />
            </button>
        </div>
    )
}

export default function SelectField(props: SelectFieldType) {
    const [searchValue, setSearchValue] = useState("");
    const { label, data, selectedValue, onChange, withSearch, onSearch, disabled, multiselect, as, searchStatus} = props;

    const showMenu = (!withSearch && !!data.length) || !!data.length;
    const selectedValueLabel = Array.isArray(selectedValue) ? selectedValue.join(", ") : selectedValue?.toString();
    const toggleLebel = as === "typehead" || multiselect ? label : (selectedValueLabel || label);

    const handleSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchValue(value);
        onSearch?.(value);
    }
    
    return (
        <Dropdown>
            {Array.isArray(selectedValue) && !!selectedValue.length && (
                <div className="custom-dropdown-selected-pill-container">
                    {selectedValue.map(value => (
                        <SelectedPill key={value} selected={value} data={data} onChange={onChange} />
                    ))}
                </div>
            )}
            <DropDownToggle {...{ disabled, as, label: toggleLebel, handleSearch }} />
            {showMenu && <Dropdown.Menu className="custom-dropdown-menu">
                {withSearch && <DropDownItem isSearch option={{ id: "search", label: "Search", value: "" }} handleSearch={handleSearch} />}
                {searchStatus === "loading" && <Dropdown.Item disabled>Loading...</Dropdown.Item>}
                {searchStatus === "error" && <Dropdown.Item disabled>Something went wrong</Dropdown.Item>}
                {data.map((option) => (
                    <DropDownItem {...{ as, multiselect, selectedValue, onChange, option, searchValue, key: option.id }} />
                ))}
            </Dropdown.Menu>}
        </Dropdown>
    );
}